import React, { useRef } from 'react';
import { calcDomainScore, getOverallScore, getTier } from '../../utils/scoring';

const Summary = ({ formData, scores }) => {
  const summaryRef = useRef(null);
  
  const printScore = calcDomainScore(scores, 'P');
  const itScore = calcDomainScore(scores, 'I');
  const cyberScore = calcDomainScore(scores, 'C');
  const overallScore = getOverallScore(scores);
  const tier = getTier(overallScore);

  const getRisks = () => {
    const risks = [];
    if (cyberScore < 3) risks.push({ icon: '🔴', text: 'Security exposure from gaps in endpoint protection, access controls, or incident response readiness' });
    if (scores.C3 && scores.C3 < 3) risks.push({ icon: '🔴', text: 'HIGH RISK: MFA not implemented across mission-critical systems' });
    if (itScore < 3) risks.push({ icon: '🟠', text: 'Reactive IT support model increasing downtime risk and operational friction' });
    if (printScore < 3) risks.push({ icon: '🟠', text: 'Unmanaged print environment causing cost leakage and potential data exposure' });
    if (scores.I3 && scores.I3 < 3) risks.push({ icon: '🟠', text: 'Backup and Disaster Recovery (BCDR) gaps threaten business continuity' });
    return risks;
  };

  const getOpps = () => {
    const opps = [];
    if (printScore < 4) opps.push({ icon: '💰', text: 'Reduce print costs 20-30% through Managed Print Services and automated fulfillment' });
    if (itScore < 4) opps.push({ icon: '📈', text: 'Improve uptime and productivity with 24/7/365 US-Based Managed IT Services' });
    if (cyberScore < 4) opps.push({ icon: '🛡️', text: 'Strengthen security posture and compliance with vCISO guidance and a 24/7 SOC' });
    opps.push({ icon: '🎯', text: 'Consolidate vendors to a Total Technology Provider for a unified, cost-effective strategy' });
    return opps;
  };

  const copyToClipboard = () => {
    if (summaryRef.current) {
      navigator.clipboard.writeText(summaryRef.current.innerText).then(() => {
        alert('Summary copied to clipboard!');
      });
    }
  };

  const risks = getRisks();
  const opps = getOpps();

  return (
    <div className="animate-fade-in">
      <div className="flex justify-end gap-3 mb-4 print:hidden">
        <button onClick={copyToClipboard} className="px-4 py-2 text-sm rounded-lg font-bold text-[#2E5FA7] border-2 border-[#2E5FA7] hover:bg-gray-50 flex items-center gap-2 transition-colors">📋 Copy Summary</button>
        <button onClick={() => window.print()} className="px-4 py-2 text-sm rounded-lg font-bold bg-[#2E5FA7] text-white hover:bg-[#244C86] flex items-center gap-2 transition-colors shadow-sm">🖨️ Print / Save PDF</button>
      </div>

      <div ref={summaryRef} className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-200 print:shadow-none print:border-none">
        <div className="bg-gradient-to-br from-[#2E5FA7] to-[#244C86] text-white p-8 text-center print:bg-white print:text-[#2E5FA7]">
          <h1 className="text-2xl font-bold mb-1">Technology Alignment Assessment</h1>
          <div className="text-sm opacity-90 font-medium tracking-wide print:opacity-100">Visual Edge IT — Executive Summary</div>
        </div>
        
        <div className="bg-[#1a3863] text-white px-8 py-4 flex flex-wrap justify-between gap-4 text-sm print:bg-gray-100 print:text-black border-y border-white/10 print:border-gray-200">
          <div><span className="font-bold text-blue-200 print:text-gray-600">Prepared for:</span> {formData.companyName || 'Client'}</div>
          <div><span className="font-bold text-blue-200 print:text-gray-600">Industry:</span> {formData.industry || 'N/A'}</div>
          <div><span className="font-bold text-blue-200 print:text-gray-600">Date:</span> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
        </div>

        <div className="p-8 space-y-8">
          <section>
            <h3 className="text-[#2E5FA7] font-bold text-lg mb-4 border-b-2 border-[#2E5FA7] pb-2 flex items-center gap-2">📋 Executive Overview</h3>
            <div className="bg-[#F4F6F9] p-6 rounded-xl text-gray-800 leading-relaxed text-sm print:bg-transparent print:p-0 border border-gray-100 print:border-none">
              <p className="mb-4 text-base">
                Based on our alignment review, <strong>{formData.companyName || 'Client'}</strong> has an overall Technology Alignment Score of{' '}
                <strong style={{ color: tier.color }}>{overallScore}/5.0 ({tier.name})</strong>.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm print:shadow-none print:border-gray-300">
                  <div className="text-xs text-gray-500 font-bold uppercase mb-1">Print & Documents</div>
                  <div className="text-xl font-bold text-[#2E5FA7]">{printScore}<span className="text-sm text-gray-400">/5.0</span></div>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm print:shadow-none print:border-gray-300">
                  <div className="text-xs text-gray-500 font-bold uppercase mb-1">IT Infrastructure</div>
                  <div className="text-xl font-bold text-[#1a5f7a]">{itScore}<span className="text-sm text-gray-400">/5.0</span></div>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm print:shadow-none print:border-gray-300">
                  <div className="text-xs text-gray-500 font-bold uppercase mb-1">Security & Compliance</div>
                  <div className="text-xl font-bold text-[#6b2d5b]">{cyberScore}<span className="text-sm text-gray-400">/5.0</span></div>
                </div>
              </div>
            </div>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 print:block print:space-y-8">
            <section>
              <h3 className="text-[#2E5FA7] font-bold text-lg mb-4 border-b-2 border-[#2E5FA7] pb-2 flex items-center gap-2">⚠️ Top Risks</h3>
              <ul className="space-y-3">
                {risks.length > 0 ? risks.map((r, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-gray-700 pb-3 border-b border-gray-100 last:border-0 font-medium">
                    <span className="mt-0.5">{r.icon}</span> <span>{r.text}</span>
                  </li>
                )) : (
                  <li className="flex items-start gap-3 text-sm text-gray-700 pb-3 font-medium"><span className="mt-0.5">✅</span> <span>No critical risks identified</span></li>
                )}
              </ul>
            </section>

            <section>
              <h3 className="text-[#2E5FA7] font-bold text-lg mb-4 border-b-2 border-[#2E5FA7] pb-2 flex items-center gap-2">💡 Top Opportunities</h3>
              <ul className="space-y-3">
                {opps.map((o, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-gray-700 pb-3 border-b border-gray-100 last:border-0 font-medium">
                    <span className="mt-0.5">{o.icon}</span> <span>{o.text}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          <section>
            <h3 className="text-[#2E5FA7] font-bold text-lg mb-4 border-b-2 border-[#2E5FA7] pb-2 flex items-center gap-2">🎯 Business Context</h3>
            <div className="bg-[#fff9e6] p-6 rounded-xl text-gray-800 text-sm print:bg-transparent print:p-0 border border-[#ffeeba] print:border-none">
              {formData.priorities || formData.friction || formData.incidents ? (
                <div className="space-y-4">
                  {formData.priorities && <div><strong className="text-[#856404]">Priorities:</strong> <span className="block mt-1">{formData.priorities}</span></div>}
                  {formData.friction && <div><strong className="text-[#856404]">Friction Points:</strong> <span className="block mt-1">{formData.friction}</span></div>}
                  {formData.incidents && <div><strong className="text-[#856404]">Recent Incidents:</strong> <span className="block mt-1">{formData.incidents}</span></div>}
                </div>
              ) : (
                <div className="italic text-gray-500">No specific context provided.</div>
              )}
            </div>
          </section>

          <section>
            <h3 className="text-[#2E5FA7] font-bold text-lg mb-4 border-b-2 border-[#2E5FA7] pb-2 flex items-center gap-2">✅ Recommended Next Steps</h3>
            <ul className="space-y-3">
              {[
                { n: '1️⃣', t: 'Schedule deep-dive discovery sessions for priority areas' },
                { n: '2️⃣', t: 'Review comprehensive vCISO and Managed Services proposals' },
                { n: '3️⃣', t: 'Validate quick wins and finalize the implementation roadmap' },
                { n: '4️⃣', t: 'Execute transition to a unified Total Technology Provider model' }
              ].map((step, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-gray-800 pb-3 border-b border-gray-100 last:border-0 font-bold">
                  <span className="mt-0.5">{step.n}</span> <span>{step.t}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="bg-gradient-to-r from-[#F4F6F9] to-white p-6 rounded-xl border border-gray-200 print:bg-transparent print:p-0 print:border-none mt-8">
            <h4 className="text-[#2E5FA7] font-bold text-base mb-2">The Visual Edge IT Advantage</h4>
            <p className="text-sm text-gray-600 leading-relaxed">
              Partnering with a <strong>Total Technology Provider</strong> means one point of accountability for your entire IT, Security, and Print environment. Benefit from our national scale, 24/7/365 US-based support, and strategic vCISO leadership, all delivered with local, personalized service.
            </p>
          </section>

          <div className="pt-6 border-t-2 border-gray-200 flex flex-wrap justify-between items-center gap-4 text-xs text-gray-500 mt-8">
            <span className="font-medium">Prepared by: {formData.preparedBy || 'Visual Edge IT'}</span>
            <span className="font-bold tracking-widest text-[#2E5FA7]">CONFIDENTIAL & PROPRIETARY</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Summary;